import styled from 'styled-components';
import ReactSelect from 'react-select';
import PropTypes from 'prop-types';

import DropdownIndicator from '../Icons/DropdownIndicator';
import { ParagraphNormal } from '../Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';

const DropdownIndicatorContainer = styled.div`
  padding-right: 12px;
`;
const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  width: ${({ width }) => width ?? '100%'};
`;
const Label = styled(ParagraphNormal)`
  line-height: 2.1;
  font-weight: 600;
`;
const ErrorMessage = styled(ParagraphNormal)`
  font-size: 0.8rem;
  margin-top: 2px;
  color: ${themeColors.colorErrorMessage};
`;

export const useSelectStyles = (minHeight) => ({
  control: (provided, state) => ({
    ...provided,
    width: state.selectProps.width || provided.width,
    border: `1px solid ${
      state.selectProps.hasError
        ? themeColors.colorErrorMessage
        : themeColors.colorBorderPrimary
    }`,
    backgroundColor: state.selectProps.isDisabled
      ? themeColors.colorBackgroundSecondaryLight
      : 'transparent',
    borderRadius: variables.borderRadius.borderRadius,
    color: themeColors.colorTextSecondary,
    fontWeight: 400,
    fontSize: variables.typography.p.small.fontSize,
    outline: 'none',
    boxShadow: 'none',
    padding: '6px',
    height: `${minHeight}px`,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    fill: state.isSelected
      ? themeColors.colorTextPrimary
      : themeColors.colorTextSecondary,
  }),
  menu: (provided) => ({
    ...provided,
    borderColor: themeColors.colorBackgroundSecondaryLight,
    borderRadius: variables.borderRadius.borderRadius,
    zIndex: 2,
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isFocused
      ? themeColors.colorBackgroundSecondaryLight
      : themeColors.colorWhite,
    color: themeColors.colorTextPrimary,
    borderLeft: state.isSelected
      ? `1px solid ${themeColors.colorTextPrimary}`
      : null,
    fontWeight: 400,
    fontSize: variables.typography.p.small.fontSize,
  }),
});

export const DropdownIndicatorIcon = ({ isFocused }) => (
  <DropdownIndicatorContainer>
    <DropdownIndicator
      color={isFocused ? themeColors.colorTextPrimary : null}
    />
  </DropdownIndicatorContainer>
);

DropdownIndicatorIcon.propTypes = {
  isFocused: PropTypes.bool,
};

const Select = ({
  width,
  options,
  label,
  error,
  onChange,
  disabled,
  autoFocus,
  isSearchable,
  defaultValue,
  dataCyButtonToggle,
  minHeight,
  ...props
}) => {
  const customStyles = useSelectStyles(minHeight);

  return (
    <InputContainer width={width}>
      {!!label && <Label>{label}</Label>}
      <ReactSelect
        isDisabled={disabled}
        styles={customStyles}
        options={options}
        width={width}
        hasError={!!error}
        defaultValue={defaultValue}
        placeholder=""
        isSearchable={isSearchable}
        components={{
          IndicatorSeparator: null,
          DropdownIndicator: DropdownIndicatorIcon,
        }}
        theme={(selectTheme) => ({
          ...selectTheme,
          colors: {
            ...selectTheme.colors,
            primary25: themeColors.colorBackgroundSecondaryLight,
            primary: themeColors.colorInputBackground,
          },
        })}
        onChange={onChange}
        autoFocus={autoFocus}
        {...props}
        data-cy={dataCyButtonToggle}
      />
      {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
    </InputContainer>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  dataCyButtonToggle: PropTypes.string,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  width: PropTypes.string,
  value: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  isSearchable: PropTypes.bool,
  defaultValue: PropTypes.string,
  minHeight: PropTypes.number,
};

export default Select;
